import { z } from "zod";

/**
 * An artist's open fan club as the Live Link and the artist's site show it
 * (LIS-1156): the club's own words and the plans fans can see. A draft club
 * is never served. A plan is free, or has a monthly price, a yearly price or
 * both (LIS-1174), once staff have turned paid plans on for the club.
 */

/**
 * The largest ticket discount a club plan can give, in whole percent
 * (LIS-1177).
 */
export const FAN_CLUB_TICKET_DISCOUNT_MAX_PERCENT = 90;

/** A plan's waitlist priority perk as the editor and the Club section name it
 *  (LIS-1181). */
export const FAN_CLUB_WAITLIST_PRIORITY_LABEL = "Waitlist priority";

/** A plan's guest places as the editor and the Club section say them
 *  (LIS-1181): "2 guest places per show". */
export const fanClubGuestPlacesLabel = (places: number) =>
  `${places} guest place${places === 1 ? "" : "s"} per show`;

/**
 * What a club discount code takes off (LIS-1178), as the artist's manager
 * says it does in their store: a whole percentage of each item's price, or
 * an amount off each item in the code's currency (never more than the
 * item's price). The checkout takes the seller's total only with exactly
 * this discount.
 */
export const clubCodeDiscountSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("percent"),
      percent: z.number().int().min(1).max(FAN_CLUB_TICKET_DISCOUNT_MAX_PERCENT),
    })
    .strict(),
  z
    .object({
      kind: z.literal("amount"),
      amountMinor: z.number().int().positive().max(10_000_000),
      currency: z.string().regex(/^[A-Z]{3}$/),
    })
    .strict(),
]);
export type ClubCodeDiscount = z.infer<typeof clubCodeDiscountSchema>;

/**
 * Where a club discount code stands (LIS-1178): `checking` until its first
 * check answers; `accepted` when the seller last took it at its discount;
 * `refused` when the seller would not take it (a check, or a fan's
 * checkout); `changed` when the seller took it at another discount. Fans
 * are given only an `accepted` code.
 */
export const CLUB_DISCOUNT_CODE_STATUSES = ["checking", "accepted", "refused", "changed"] as const;
export type ClubDiscountCodeStatus = (typeof CLUB_DISCOUNT_CODE_STATUSES)[number];

/**
 * Whether a fan can join a plan now: `open`; `closed` while the artist has
 * stopped new members joining; `full` once its member limit is reached.
 */
export const fanClubPlanEnrollmentSchema = z.enum(["open", "closed", "full"]);

/** How often a paid plan's price renews (LIS-1174). */
export const fanClubPlanIntervalSchema = z.enum(["month", "year"]);

/** The cheapest and dearest price a paid plan can have, in minor units of
 *  its currency (LIS-1174). */
export const FAN_CLUB_PLAN_PRICE_MIN_MINOR = 100;
export const FAN_CLUB_PLAN_PRICE_MAX_MINOR = 100_000;

/** The currencies a paid plan can be priced in: the artist's own when it is
 *  one of these, pounds otherwise (LIS-1174). */
export const FAN_CLUB_PLAN_CURRENCIES = ["GBP", "EUR", "USD"] as const;

/**
 * One price of a paid plan (LIS-1174): the engine plan a join of it names,
 * how often it renews, and what each period costs, VAT included. A plan
 * with a monthly and a yearly price is two engine plans the fan chooses
 * between; a fan holds one of them.
 */
export const fanClubPlanPriceSchema = z.object({
  planId: z.string().min(1),
  interval: fanClubPlanIntervalSchema,
  priceMinor: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
});

export const fanClubPlanSchema = z.object({
  /** The engine plan's id: what a join names. */
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  enrollment: fanClubPlanEnrollmentSchema,
  /** The whole percentage off the artist's tickets Lissen sells that the
   *  plan's members get (LIS-1177); null for none. Absent from a page
   *  cached before discounts. */
  ticketDiscountPercent: z
    .number()
    .int()
    .min(1)
    .max(FAN_CLUB_TICKET_DISCOUNT_MAX_PERCENT)
    .nullable()
    .optional(),
  /** Whether the plan's members are offered a returned ticket of the
   *  artist's sold-out events Lissen sells before other fans on its waitlist
   *  (LIS-1181). Absent from a page cached before it. */
  waitlistPriority: z.boolean().optional(),
  /** How many guests each member may invite to each of the artist's events
   *  Lissen sells (LIS-1181); null for none. Absent from a page cached
   *  before it. */
  guestPlaces: z.number().int().min(1).nullable().optional(),
  /** Whether the plan's members can buy the artist's members' presales.
   *  Absent from a page cached before it. */
  presaleAccess: z.boolean().optional(),
  /** A paid plan's prices (LIS-1174), monthly first; empty for a free plan.
   *  Absent from a page cached before paid plans. */
  prices: z.array(fanClubPlanPriceSchema).max(2).optional(),
});

export const fanClubSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  /** The club's active plans, in the artist's order. */
  plans: z.array(fanClubPlanSchema).min(1),
});

/**
 * The signed-in fan's current membership of the page's club: the plan it is
 * of (the plan's id, whichever of its prices they pay). Null when they hold
 * none, or the club is not open.
 */
export const fanClubMembershipSchema = z.object({
  membership: z
    .object({
      planId: z.string().min(1),
      /** When the engine started it. */
      startedAt: z.string().nullable(),
      /** A paid membership (LIS-1174): its payment method can be changed. */
      paid: z.boolean().optional(),
      /** When a paid membership renews; null once it ends instead. */
      renewsAt: z.string().nullable().optional(),
      /** When a paid membership the fan left ends: the end of the period
       *  they paid for (LIS-1174). Null while it renews. */
      endsAt: z.string().nullable().optional(),
      /** A paid membership whose renewal failed after its benefit grace
       *  (LIS-1174): it gives no perks until it is paid, and its payment
       *  method can be changed. */
      paymentDue: z.boolean().optional(),
    })
    .nullable(),
});

/**
 * A paid join's first payment (LIS-1174): the engine's membership, awaiting
 * it, and what the purchase sheet confirms it with, the first invoice's
 * PaymentIntent on the account that sells the club's plans: a connected
 * account, or Lissen's platform account (`connectedAccountId` null, LIS-1176).
 */
export const fanClubPaymentSchema = z.object({
  membershipId: z.string().min(1),
  clientSecret: z.string().min(1),
  publishableKey: z.string().min(1),
  connectedAccountId: z.string().min(1).nullable(),
});

/**
 * What a join answers: the fan's membership, made at once for a free plan;
 * for a paid one, the payment that makes it (`payment`), with the
 * membership null until it is paid. A free join's `payment` is null.
 */
export const fanClubJoinedSchema = fanClubMembershipSchema.extend({
  payment: fanClubPaymentSchema.nullable().optional(),
});

/** The signed-in fan paid a join (LIS-1174): the membership it made, read
 *  again from the engine. `viewerId` as for a join. */
export const fanClubPaidRequestSchema = z.object({
  viewerId: z.string().min(1).max(128),
  membershipId: z.string().min(1).max(200),
});

/** The signed-in fan changes the card their paid membership is billed with:
 *  the engine's hosted page, which returns them to `returnUrl`. */
export const fanClubPaymentMethodRequestSchema = z.object({
  viewerId: z.string().min(1).max(128),
  returnUrl: z.string().url().max(2000),
});
export const fanClubPaymentMethodSchema = z.object({ url: z.string().url() });

/** A signed-in fan joins one of the club's plans. */
export const fanClubJoinRequestSchema = z.object({
  /** The signed-in fan the page showed when Join was pressed: refused
   *  (`viewer_changed`) when the session now names someone else. */
  viewerId: z.string().min(1).max(128),
  planId: z.string().min(1).max(200),
  /** A new UUID per press of Join, sent again on a retry, so a retried join
   *  makes one membership. */
  requestId: z.string().uuid(),
});

/** A signed-in fan leaves the club; `viewerId` as for a join. */
export const fanClubLeaveRequestSchema = z.object({
  viewerId: z.string().min(1).max(128),
});

/**
 * Why a join was refused: the club is not open or the plan is not one of
 * its plans (`club_unavailable`), the plan is closed or full, or the fan
 * already holds a membership of another of the club's plans and must leave
 * it first (`already_member`). A paid join is refused as `payment_pending`
 * while the fan's unpaid join of another of the club's prices still waits
 * for its payment (LIS-1174). A join or leave pressed for one fan while
 * the session has since signed in someone else (another tab) is refused as
 * `viewer_changed`.
 */
export const fanClubJoinRefusalSchema = z.object({
  error: z.enum([
    "club_unavailable",
    "plan_closed",
    "plan_full",
    "already_member",
    "payment_pending",
    "viewer_changed",
  ]),
});

/** A club plan as Lissen's copy holds it, with its current members: one
 *  engine plan, which may be one price of a plan the club shows
 *  (`FanClubShownPlan`). */
export type FanClubPlanCopy = {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  enrollmentOpen: boolean;
  memberCap: number | null;
  /** Fans whose membership of the plan is current. */
  members: number;
  /**
   * Whether a new fan can join, as the engine's plan read last said: `full`
   * once its member cap's places are taken. Null on a copy the engine's
   * enrollment has not reached yet.
   */
  enrollment: FanClubPlanEnrollment | null;
  /** Places still available under the member cap; null with no cap. */
  placesLeft: number | null;
  /** Whether the plan's members can buy the artist's members' presales;
   *  set on Lissen's copy, absent where a reader builds a plan itself. */
  presaleAccess?: boolean;
  /** The whole percentage off the artist's tickets Lissen sells that the
   *  plan's members get (LIS-1177); null for none. Set on Lissen's copy,
   *  absent where a reader builds a plan itself. */
  ticketDiscountPercent?: number | null;
  /** Whether the plan's members come first on the waitlists of the artist's
   *  tickets Lissen sells (LIS-1181). Set on Lissen's copy, absent where a
   *  reader builds a plan itself. */
  waitlistPriority?: boolean;
  /** How many guests each member may invite to each of the artist's events
   *  Lissen sells (LIS-1181); null for none. Set on Lissen's copy, absent
   *  where a reader builds a plan itself. */
  guestPlaces?: number | null;
  /** Absent where the reader knows every plan is free. */
  priceMinor?: number;
  /** The price's currency; absent with `priceMinor`. */
  currency?: string;
  /** How often a paid plan renews; null or absent for a free plan. */
  interval?: FanClubPlanInterval | null;
  /** The shown plan this engine plan is a price of, by its id; null or
   *  absent for the shown plan's own row (LIS-1174). */
  priceOf?: string | null;
  /** A price a newer one replaced: its members keep it, nobody joins it. */
  superseded?: boolean;
  /** When the engine last changed the plan, which orders a plan's prices. */
  updatedAt?: string;
  /** Whether attendance earns the plan; such a plan is never joined. */
  earned?: boolean;
};

export type FanClubPlanPrice = z.infer<typeof fanClubPlanPriceSchema>;

/**
 * A plan as the club shows it (LIS-1174), from its engine plans' copies: the
 * plan's own row's fields and perks, its current members across every
 * price, and its current prices. Free: `prices` is empty and the plan's own
 * row is what fans join.
 */
export type FanClubShownPlan = Omit<
  FanClubPlanCopy,
  "priceMinor" | "currency" | "interval" | "priceOf" | "superseded" | "updatedAt"
> & {
  /** The plan's current paid prices, monthly first; empty for a free plan. */
  prices: FanClubPlanPrice[];
};

const INTERVAL_ORDER: Record<FanClubPlanInterval, number> = { month: 0, year: 1 };

/**
 * The club's plans as it shows them (LIS-1174), from its plan copies in the
 * artist's order: each shown plan's own row with the copies that are its
 * prices. A plan's current prices are its rows no newer price replaced: a
 * free plan's own row, or its monthly and yearly prices. Its members are
 * every row's; its switches (enrollment, member limit) are those of its
 * current prices, which a save keeps alike.
 */
export function fanClubShownPlans(
  copies: readonly FanClubPlanCopy[],
): FanClubShownPlan[] {
  const leads = copies.filter((copy) => !copy.priceOf);
  return leads.map((lead) => {
    const rows = copies.filter(
      (copy) => copy.id === lead.id || copy.priceOf === lead.id,
    );
    const current = rows.filter((row) => !row.superseded);
    // One price per interval: should a replaced price not have been marked
    // yet (its save failed part way), the newest is the plan's.
    const paid = current
      .filter((row) => (row.priceMinor ?? 0) > 0 && row.interval)
      .sort(
        (a, b) =>
          INTERVAL_ORDER[a.interval!] - INTERVAL_ORDER[b.interval!] ||
          (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "") ||
          b.id.localeCompare(a.id),
      )
      .filter(
        (row, index, sorted) =>
          index === 0 || sorted[index - 1]!.interval !== row.interval,
      );
    const switches = paid[0] ?? current[0] ?? lead;
    const {
      priceMinor: _price,
      currency: _currency,
      interval: _interval,
      priceOf: _priceOf,
      superseded: _superseded,
      updatedAt: _updatedAt,
      ...own
    } = lead;
    return {
      ...own,
      enrollmentOpen: switches.enrollmentOpen,
      memberCap: switches.memberCap,
      enrollment: switches.enrollment,
      placesLeft: switches.placesLeft,
      members: rows.reduce((members, row) => members + row.members, 0),
      prices: paid.map((row) => ({
        planId: row.id,
        interval: row.interval!,
        priceMinor: row.priceMinor!,
        currency: row.currency ?? "GBP",
      })),
    };
  });
}

/**
 * Whether fans can join a plan now, as the engine's enrollment says (the
 * engine counts a cap's places; Lissen never does). A plan attendance earns
 * is never joined; a copy the engine's enrollment has not reached yet is
 * open or closed by its own switch.
 */
export function fanClubPlanEnrollment(
  plan: Pick<
    FanClubPlanCopy,
    "earned" | "enrollment" | "enrollmentOpen"
  >,
): FanClubPlanEnrollment {
  if (plan.earned) return "closed";
  return plan.enrollment ?? (plan.enrollmentOpen ? "open" : "closed");
}

/**
 * The club as fans see it, from its copy and its shown plans in the
 * artist's order (`fanClubShownPlans`): its active plans, each with its
 * prices. A paid plan is closed to new members while staff have turned paid
 * plans off for the club (`paidPlansEnabled`, LIS-1174); it still shows, so
 * its members keep Leave and their payment method. Null when it has none to
 * show.
 * Whether the club is open is the caller's: the Live Link and the site
 * serve only an open club, and an editor's preview shows a draft too.
 */
export function fanClubForFans(
  club: {
    name: string;
    description: string | null;
    paidPlansEnabled?: boolean;
  },
  plans: readonly FanClubShownPlan[],
): FanClub | null {
  const shown = plans
    .filter((plan) => plan.active)
    .map((plan) => ({
      id: plan.id,
      name: plan.name,
      description: plan.description,
      enrollment:
        plan.prices.length > 0 && !club.paidPlansEnabled
          ? ("closed" as const)
          : fanClubPlanEnrollment(plan),
      ticketDiscountPercent: plan.ticketDiscountPercent ?? null,
      waitlistPriority: plan.waitlistPriority ?? false,
      guestPlaces: plan.guestPlaces ?? null,
      presaleAccess: plan.presaleAccess ?? false,
      prices: plan.prices,
    }));
  return shown.length > 0
    ? { name: club.name, description: club.description, plans: shown }
    : null;
}

export type FanClubPlanEnrollment = z.infer<typeof fanClubPlanEnrollmentSchema>;
export type FanClubPlanInterval = z.infer<typeof fanClubPlanIntervalSchema>;
export type FanClubPayment = z.infer<typeof fanClubPaymentSchema>;
export type FanClubJoined = z.infer<typeof fanClubJoinedSchema>;
export type FanClubPaidRequest = z.infer<typeof fanClubPaidRequestSchema>;
export type FanClubPaymentMethodRequest = z.infer<
  typeof fanClubPaymentMethodRequestSchema
>;
export type FanClubPlan = z.infer<typeof fanClubPlanSchema>;
export type FanClub = z.infer<typeof fanClubSchema>;
export type FanClubMembership = z.infer<typeof fanClubMembershipSchema>;
export type FanClubJoinRequest = z.infer<typeof fanClubJoinRequestSchema>;
export type FanClubLeaveRequest = z.infer<typeof fanClubLeaveRequestSchema>;
export type FanClubJoinRefusal = z.infer<
  typeof fanClubJoinRefusalSchema
>["error"];
